import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Snackbar,
  Alert,
  Divider,
} from "@mui/material";
import { searchPaciente, changeUserPassword } from "../../api/apiService";
import { cpf as cpfValidator } from "cpf-cnpj-validator";
import Navbar from "../../components/Navbar";

const ChangePasswordScreen = () => {
  const [cpf, setCpf] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [paciente, setPaciente] = useState(null);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [error, setError] = useState({});

  const formatCPF = (cpf) => {
    return cpf
      .replace(/\D/g, "")
      .slice(0, 11)
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{2})$/, "$1-$2");
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;

    if (name === "newPassword") {
      setNewPassword(value);
      if (value.length < 8) {
        setError((prevErrors) => ({
          ...prevErrors,
          newPassword: "A senha deve ter pelo menos 8 caracteres.",
        }));
      } else {
        setError((prevErrors) => ({ ...prevErrors, newPassword: "" }));
      }
    } else {
      setConfirmPassword(value);
    }

    if (newPassword !== value) {
      setError((prevErrors) => ({
        ...prevErrors,
        confirmPassword: "As senhas não coincidem.",
      }));
    } else {
      setError((prevErrors) => ({ ...prevErrors, confirmPassword: "" }));
    }
  };

  const handleSearch = async () => {
    if (!cpfValidator.isValid(cpf.replace(/\D/g, ""))) {
      setError({ cpf: "CPF inválido!" });
      return;
    }
    setError({});
    setLoading(true);
    try {
      const data = await searchPaciente(cpf);
      setPaciente(data);
      setAlert({
        open: true,
        message: "Paciente encontrado!",
        severity: "success",
      });
    } catch (error) {
      setPaciente(null);
      setAlert({
        open: true,
        message: "Paciente não encontrado.",
        severity: "error",
      });
      setCpf("");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 8) {
      setError({ confirmPassword: "A senha deve ter no mínimo 8 caracteres." });
      return;
    }

    if (newPassword !== confirmPassword) {
      setError({ confirmPassword: "As senhas não coincidem." });
      return;
    }

    setError({});
    try {
      const response = await changeUserPassword(cpf, newPassword);

      if (response.error) {
        setAlert({ open: true, message: response.error, severity: "error" });
      } else {
        setAlert({
          open: true,
          message: "Senha alterada com sucesso!",
          severity: "success",
        });
        setNewPassword("");
        setConfirmPassword("");
        setCpf("");
        setPaciente(null);
      }
    } catch (error) {
      setAlert({
        open: true,
        message: "Erro ao alterar a senha.",
        severity: "error",
      });
      setCpf("");
    }
  };

  const isPasswordValid =
    newPassword.length >= 8 && newPassword === confirmPassword;

  return (
    <>
      <Navbar />
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100vh"
      >
        <Card style={{ maxWidth: 700, width: "100%", padding: "10px" }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Alterar Senha do Usuário
            </Typography>

            <TextField
              label="CPF"
              value={cpf}
              onChange={(e) => {
                setCpf(formatCPF(e.target.value));
                if (!cpfValidator.isValid(e.target.value.replace(/\D/g, ""))) {
                  setError({ cpf: "CPF inválido!" });
                } else {
                  setError({});
                }
              }}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  cpfValidator.isValid(cpf.replace(/\D/g, ""))
                ) {
                  handleSearch();
                }
              }}
              fullWidth
              margin="dense"
              error={!!error.cpf}
              helperText={error.cpf || ""}
              disabled={!!paciente}
            />

            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              fullWidth
              disabled={
                loading ||
                !!paciente ||
                !cpfValidator.isValid(cpf.replace(/\D/g, "")) ||
                cpf.trim() === ""
              }
              style={{ margin: "10px 0" }}
            >
              {loading ? <CircularProgress size={24} /> : "Buscar Paciente"}
            </Button>

            {paciente && (
              <>
                <Divider style={{ margin: "10px 0" }} />
                <Box display="flex" justifyContent="space-between">
                  <Box><strong>Nome:</strong> {paciente.nomeCompleto}</Box>
                  <Box><strong>Email:</strong> {paciente.email || "Não informado"}</Box>
                </Box>
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Box><strong>UBS:</strong> {paciente.nomeUbs || "Não informado"}</Box>
                  <Box><strong>Endereço:</strong> {`${paciente.endereco}, ${paciente.numero}, ${paciente.bairro}, ${paciente.cidade} / ${paciente.estado}`}</Box>
                </Box>
                <Divider style={{ margin: "10px 0" }} />

                <TextField
                  label="Nova Senha"
                  type="password"
                  value={newPassword}
                  name="newPassword"
                  onChange={handlePasswordChange}
                  fullWidth
                  margin="dense"
                  error={!!error.newPassword}
                  helperText={error.newPassword}
                />
                <TextField
                  label="Confirmar Nova Senha"
                  type="password"
                  value={confirmPassword}
                  name="confirmPassword"
                  onChange={handlePasswordChange}
                  fullWidth
                  margin="dense"
                  error={!!error.confirmPassword}
                  helperText={error.confirmPassword}
                />
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleChangePassword}
                  fullWidth
                  disabled={!isPasswordValid}
                  style={{ marginTop: "10px" }}
                >
                  Alterar Senha
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <Snackbar
          open={alert.open}
          autoHideDuration={6000}
          onClose={() => setAlert({ ...alert, open: false })}
        >
          <Alert
            onClose={() => setAlert({ ...alert, open: false })}
            severity={alert.severity}
          >
            {alert.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default ChangePasswordScreen;
